<template>
  <v-layout class="full-height-container" justify-center align-center>
    <v-layout class="form-container">
      <div class="form">
        <div v-show="true">
          <h2 v-if="currentIdentity">
            Hi,
            <span class="ph-no-capture">{{ currentIdentity.name }}</span>
          </h2>

          <div v-if="blocked" class="text-xs-left py-2 body-1">
            <v-alert :value="true" type="error" class="mb-3">
              You have exceeded the maximum number of identity verification
              attempts.
            </v-alert>
            <div class="pb-2">
              Please contact your tax firm to verify your identity using an
              alternative method before signing.
            </div>
          </div>

          <div v-else-if="verifying" class="text-xs-left py-2 body-1">
            <div class="text-xs-center py-4">
              <v-progress-circular
                indeterminate
                color="primary"
                size="48"
                class="mb-3"
              />
              <div class="body-1 font-weight-medium">
                Verifying your identity...
              </div>
              <div class="caption grey--text text--darken-1 mt-1">
                This usually takes less than a minute. Please don't close this
                page.
              </div>
            </div>
          </div>

          <div v-else-if="pollTimedOut" class="text-xs-left py-2 body-1">
            <v-alert :value="true" type="warning" class="mb-3">
              Verification is taking longer than expected.
            </v-alert>
            <div class="pb-2">
              Your identity verification is still being processed. Please
              refresh the page to check for updates, or contact your tax firm
              for assistance.
            </div>
            <v-btn
              block
              large
              color="primary"
              class="mt-3 text-none"
              @click="$router.go(0)"
            >
              <b>Refresh Page</b>
            </v-btn>
          </div>

          <div v-else-if="showPhoneInput" class="text-xs-left py-2 body-1">
            <div class="pb-2">
              Please enter your phone number to continue with identity
              verification.
            </div>
            <v-text-field
              v-model="phoneNumber"
              label="Phone number"
              placeholder="+1 (555) 123-4567"
              outline
              :error-messages="phoneError ? [phoneError] : []"
              @input="phoneError = ''"
            />
            <v-btn
              block
              large
              color="primary"
              class="mt-1 text-none"
              :loading="savingPhone"
              @click="savePhoneAndRetry"
            >
              <b>Save & Continue</b>
            </v-btn>
          </div>

          <div v-else class="text-xs-left py-2 body-1">
            <div class="pb-2">
              Before continuing, we need to first verify your identity. You'll
              be asked to complete the following steps:
            </div>
            <ul class="pl-4 pb-2">
              <li>
                Take a photo of a government-issued ID (such as a driver's
                license)
              </li>
              <li>Take a quick selfie to confirm it's really you</li>
            </ul>
            <div class="pb-2">
              This process typically takes less than 2 minutes.
            </div>
          </div>

          <v-btn
            v-if="!blocked && !verifying && !pollTimedOut && !showPhoneInput"
            block
            large
            color="primary"
            class="mt-3 text-none"
            :loading="loading"
            @click="createVerificationSession"
          >
            <b>Start Identity Verification</b>
          </v-btn>

          <div
            v-if="tmpIdvOverride || blocked"
            class="mt-3 alternative-verification"
          >
            <div class="body-1 font-weight-medium">
              Can't complete online identity verification?
            </div>

            <template v-if="tmpIdvManualUpload">
              <v-btn
                block
                outline
                color="primary"
                class="text-none mb-3"
                :loading="printLoading"
                @click="printSignaturePages"
              >
                <v-icon small class="mr-2">print</v-icon>
                Print Signature Pages
              </v-btn>

              <!-- File upload section -->
              <input
                type="file"
                ref="uploadInput"
                accept=".pdf,.jpg,.jpeg,.png"
                @click="$refs.uploadInput.value = ''"
                @input="onFileSelected($event)"
                hidden
                multiple
              />

              <div class="upload-section">
                <div class="body-1 font-weight-medium mb-1">
                  Upload signature forms
                </div>
                <div class="caption grey--text text--darken-1 mb-2">
                  All forms must be signed by hand otherwise your tax return
                  e-file may be delayed.
                </div>

                <v-btn
                  block
                  outline
                  color="secondary"
                  class="text-none mb-2"
                  :loading="uploading"
                  @click="$refs.uploadInput.click()"
                >
                  <v-icon small class="mr-2">cloud_upload</v-icon>
                  Select files to upload
                </v-btn>

                <!-- Uploaded files list -->
                <div
                  v-for="(file, index) in uploadedFiles"
                  :key="index"
                  class="uploaded-file"
                >
                  <v-icon small color="green" class="mr-2">check_circle</v-icon>
                  <span class="body-2 uploaded-file__name">{{
                    file.filename
                  }}</span>
                  <v-btn
                    flat
                    icon
                    small
                    color="grey"
                    @click="removeFile(index)"
                  >
                    <v-icon small>close</v-icon>
                  </v-btn>
                </div>
              </div>

              <v-btn
                block
                large
                color="primary"
                class="text-none mt-3"
                :loading="submitting"
                :disabled="uploadedFiles.length === 0"
                @click="completeHandSignature"
              >
                <b>Complete hand signature</b>
              </v-btn>
            </template>
            <div v-else class="body-1 grey--text text--darken-1">
              Contact your tax firm to verify your identity using an alternative
              method.
            </div>
          </div>

          <v-btn
            flat
            block
            color="secondary"
            class="text-none mt-2"
            @click="goHome"
          >
            <v-icon small class="mr-2">arrow_back</v-icon>
            Go back
          </v-btn>
        </div>

        <div v-show="false">
          Thank you for completing the identity verification.
        </div>
      </div>
    </v-layout>
  </v-layout>
</template>

<script src="https://js.stripe.com/v3/"></script>
<script>
import axios from 'axios';
import { PDFDocument } from 'pdf-lib';
import { mapGetters } from 'vuex';
import API from '@/api';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';

const MAX_FAILED_ATTEMPTS = 3;
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 90000;
const TERMINAL_STATUSES = [
  'failed',
  'canceled',
  'declined',
  'not_supported',
  'requires_input',
  'incomplete',
];

export default {
  mixins: [FirmPreferenceMixins],
  data() {
    return {
      loading: false,
      verifying: false,
      printLoading: false,
      uploading: false,
      submitting: false,
      uploadedFiles: [],
      blocked: false,
      pollTimer: null,
      pollTimeoutTimer: null,
      pollTimedOut: false,
      showPhoneInput: false,
      phoneNumber: '',
      phoneError: '',
      savingPhone: false,
    };
  },
  props: {
    currentIdentity: {
      type: Object,
      default: () => {},
    },
    taxReturnId: {
      required: true,
    },
    taxReturn: {
      type: Object,
      default: () => null,
    },
    companyId: {
      type: String,
      default: () => null,
    },
    stripeIdentityVerifications: {
      type: Array,
      default: () => [],
    },
  },
  created() {
    this.stripe = Stripe(process.env.STRIPE_PUBLISHABLE_KEY);
    this.checkBlockedStatus();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  computed: {
    ...mapGetters({
      currentUser: 'user',
    }),

    signaturePages() {
      if (!this.taxReturn || !this.taxReturn.e_sign_forms) return [];
      return this.taxReturn.e_sign_forms.filter(
        form =>
          form.signatures &&
          form.signatures.some(sig => sig.userId === this.currentUser.id),
      );
    },
  },
  methods: {
    checkBlockedStatus(verifications) {
      const userId = this.currentUser && this.currentUser.id;
      if (!userId) return;

      const records = verifications || this.stripeIdentityVerifications;
      const failedCount = records.filter(
        v => v.user_id === userId && v.status === 'failed',
      ).length;

      this.blocked = failedCount >= MAX_FAILED_ATTEMPTS;
    },
    goHome() {
      this.$router.push('/');
    },

    async savePhoneAndRetry() {
      const phone = this.phoneNumber.trim();
      if (!phone) {
        this.phoneError = 'Please enter a phone number.';
        return;
      }

      this.savingPhone = true;
      this.phoneError = '';
      try {
        await API.user.updateUser({
          id: this.currentUser.id,
          phone_number: phone,
        });
        await this.$store.dispatch('getCurrentUser');
        this.showPhoneInput = false;
        this.createVerificationSession();
      } catch (error) {
        this.phoneError =
          'There was a problem saving your phone number. Please try again.';
      } finally {
        this.savingPhone = false;
      }
    },

    async createVerificationSession() {
      this.loading = true;
      axios
        .post('stripe_identity_verifications', {
          tax_return_id: this.taxReturnId,
        })
        .then(response => {
          return this.stripe.verifyIdentity(response.data.client_secret);
        })
        .then(result => {
          if (result.error) {
            this.$store.dispatch('application/setErrorNotification', {
              message: result.error.message,
            });
          } else {
            // User submitted verification to Stripe, but it's not confirmed yet.
            // Poll the backend until the webhook confirms the status.
            this.loading = false;
            this.verifying = true;
            this.pollTimedOut = false;
            this.startPollingVerificationStatus();
          }
        })
        .catch(error => {
          const data = error.response && error.response.data;
          if (
            error.response &&
            error.response.status === 403 &&
            data &&
            data.blocked
          ) {
            this.blocked = true;
          } else if (
            error.response &&
            error.response.status === 422 &&
            data &&
            (data.error === 'phone_missing' || data.error === 'phone_invalid')
          ) {
            this.phoneNumber = data.phone_number || '';
            this.phoneError =
              data.error === 'phone_invalid'
                ? 'This phone number does not appear to be valid. Please try a different number.'
                : '';
            this.showPhoneInput = true;
          } else {
            this.$store.dispatch('application/setErrorNotification', {
              message:
                'There was an error starting identity verification. Please try again.',
            });
          }
        })
        .finally(() => {
          if (!this.verifying) {
            this.loading = false;
          }
        });
    },

    startPollingVerificationStatus() {
      this.stopPolling();

      this.pollVerificationStatus();

      this.pollTimer = setInterval(() => {
        this.pollVerificationStatus();
      }, POLL_INTERVAL_MS);

      this.pollTimeoutTimer = setTimeout(() => {
        this.stopPolling();
        this.pollTimedOut = true;
        this.verifying = false;
      }, POLL_TIMEOUT_MS);
    },

    async pollVerificationStatus() {
      try {
        const response = await axios.get('stripe_identity_verifications');
        const verifications = response.data || [];
        const userId = this.currentUser.id;

        const matches = verifications.filter(
          v => String(v.user_id) === String(userId),
        );

        if (matches.length === 0) return;

        // If any verification for this user is verified, we're good.
        if (matches.some(v => v.status === 'verified')) {
          this.stopPolling();
          this.verifying = false;
          this.$emit('identity-verified', true);
          return;
        }

        // Check the most recent verification for terminal failure.
        const sorted = matches
          .slice()
          .sort(
            (a, b) =>
              new Date(a.stripe_verification_session_created_at) -
              new Date(b.stripe_verification_session_created_at),
          );
        const latest = sorted[sorted.length - 1];
        if (TERMINAL_STATUSES.includes(latest.status)) {
          this.stopPolling();
          this.verifying = false;
          this.checkBlockedStatus(matches);

          const description = latest.error_description || 'Please try again.';
          this.$store.dispatch('application/setErrorNotification', {
            message: `Identity verification failed. ${description}`,
          });
        }
      } catch (e) {
        // Silently retry on network errors; the timeout will catch persistent failures.
      }
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
      if (this.pollTimeoutTimer) {
        clearTimeout(this.pollTimeoutTimer);
        this.pollTimeoutTimer = null;
      }
    },

    async printSignaturePages() {
      if (!this.taxReturn || this.signaturePages.length === 0) return;

      this.printLoading = true;
      try {
        const response = await API.integration.downloadDocument({
          companyId: this.companyId,
          link: this.taxReturn.watermarked_file,
        });

        const pdfSrc = response.data.document.data.link;
        const pdfBytes = await fetch(pdfSrc).then(res => res.arrayBuffer());
        const pdfDoc = await PDFDocument.load(pdfBytes);

        const pageIndices = this.signaturePages.map(form => form.page - 1);
        const printPdf = await PDFDocument.create();
        const pages = await printPdf.copyPages(pdfDoc, pageIndices);
        pages.forEach(page => printPdf.addPage(page));

        const printBytes = await printPdf.save();
        const blob = new Blob([printBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);

        const printWindow = window.open(url);
        if (printWindow) {
          printWindow.addEventListener('load', () => {
            printWindow.print();
          });
        }
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem loading the signature pages.',
        });
        console.error('Print error:', error);
      } finally {
        this.printLoading = false;
      }
    },

    async onFileSelected(e) {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;

      this.uploading = true;
      try {
        for (const file of files) {
          const {
            data: { id, upload_link },
          } = await API.integration.getTemporaryUploadLink({
            companyId: this.companyId,
          });

          await API.integration.uploadToDropbox(upload_link, file);

          const response = await API.integration.processDocument({
            id,
            documentName: file.name,
            companyId: this.companyId,
            path: '/Client Hand Signed Forms',
          });

          const list = response.data.document_location.split('/');
          list.splice(0, 3);
          const documentPath = list.join('/');

          this.uploadedFiles.push({
            path: documentPath,
            filename: file.name,
          });
        }
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem uploading the file.',
        });
        console.error('Upload error:', error);
      } finally {
        this.uploading = false;
      }
    },

    removeFile(index) {
      this.uploadedFiles.splice(index, 1);
    },

    async completeHandSignature() {
      this.submitting = true;
      try {
        await axios.post(
          `/tax_returns/${this.taxReturnId}/complete_hand_signature`,
          {
            company_id: this.companyId,
            hand_signed_files: this.uploadedFiles,
          },
        );
        this.$emit('hand-sign-completed');
      } catch (error) {
        const message =
          (error.response &&
            error.response.data &&
            error.response.data.error) ||
          'There was a problem submitting your signed forms.';
        this.$store.dispatch('application/setErrorNotification', { message });
        console.error('Submit error:', error);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="postcss" scoped>
.full-height-container {
  height: 100vh;
}

.form-container {
  max-width: 400px;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0 0 0 / 25%);
  background-color: white;
}

.form {
  padding: 12px 16px;
  overflow: hidden;

  & >>> h2 {
    padding: 10px 0px;
  }

  & >>> .v-input__slot {
    height: 44px;
  }
}

.upload-section {
  padding: 12px;
  border: 1px dashed #ccc;
  border-radius: 4px;
  background-color: #fafafa;
}

.uploaded-file {
  display: flex;
  align-items: center;
  padding: 4px 0;
}

.uploaded-file__name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
